import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import {
  Shield,
  Clock,
  LifeBuoy,
  Infinity,
  Globe,
  PiggyBank,
  Home,
  Stethoscope,
  HeartPulse,
  Users,
  Plane,
  GraduationCap,
  Wallet,
  Banknote,
} from "lucide-react";
import disabilityIcon from "../../../assets/disabilityIcon.jpg";

function SectionTitle({ children }) {
  return (
    <div className="text-center mb-10 sm:mb-12 mt-12 sm:mt-16">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#49628b] font-[Montserrat] tracking-wide">
        {children}
      </h2>
    </div>
  );
}

function CategoryTitle({ children }) {
  return (
    <div className="text-center mt-12 sm:mt-16 mb-6 sm:mb-8">
      <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] font-[Montserrat] tracking-wide">
        {children}
      </h3>
    </div>
  );
}

// Grid
function CardGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
      {items.map(({ title, icon: Icon, image, to = "#" }) => (
        <Link
          key={title}
          to={to}
          className="group bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-200 p-5 sm:p-6 flex flex-col items-center justify-center text-center min-h-[120px] sm:min-h-[150px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a4b8c] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
          aria-label={title}
        >
          {image ? (
            <img
              src={image}
              alt={title}
              className="w-10 h-10 sm:w-12 sm:h-12 mb-3 object-contain rounded-md"
            />
          ) : (
            <span className="mb-3 inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#1a4b8c]">
              {Icon && (
                <Icon
                  aria-hidden="true"
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                />
              )}
            </span>
          )}
          <p className="font-medium text-gray-800 leading-snug text-[15px] sm:text-[16px] group-hover:text-[#1a4b8c]">
            {title}
          </p>
        </Link>
      ))}
    </div>
  );
}

export default function EverythingWeSell() {
  const life = [
    { title: "Assurance vie", icon: Shield, to: "/life-insurance" },
    { title: "Assurance vie temporaire", icon: Clock, to: "/term-life-insurance" },
    {
      title: "Assurance vie permanente",
      icon: LifeBuoy,
      to: "/permanent-life-insurance",
    },
    {
      title: "Assurance vie entière",
      icon: Infinity,
      to: "/whole-life-insurance",
    },
    {
      title: "Assurance vie universelle",
      icon: Globe,
      to: "/universal-life-insurance",
    },
    {
      title: "Assurance vie permanente avec participation",
      icon: PiggyBank,
      to: "/participating-permanent-life-insurance",
    },
    { title: "Assurance hypothécaire", icon: Home, to: "/mortgage-insurance" },
    {
      title: "Assurance sans examen médical",
      icon: Stethoscope,
      to: "/no-medical-insurance",
    },
    { title: "Assurance pour enfants", icon: Users, to: "/children-insurance" },
    { title: "Assurance maladie", icon: HeartPulse, to: "/health-insurance" },
  ];

  const travel = [
    {
      title: "Assurance visiteurs au Canada",
      icon: Plane,
      to: "/visitor-to-canada-insurance",
    },
    {
      title: "Assurance super visa",
      icon: Shield,
      to: "/super-visa-insurance",
    },
    {
      title: "Assurance nouveaux arrivants",
      icon: Globe,
      to: "/newcomers-insurance",
    },
    {
      title: "Assurance étudiants internationaux",
      icon: GraduationCap,
      to: "/international-student-insurance",
    },
    { title: "Assurance voyage", icon: Plane, to: "/travel-insurance" },
  ];

  const livingBenefits = [
    {
      title: "Assurance maladies graves",
      icon: HeartPulse,
      to: "/critical-illness-insurance",
    },
    {
      title: "Assurance invalidité",
      image: disabilityIcon,
      to: "/disability-insurance",
    },
  ];

  const investments = [
    {
      title: "Compte d'épargne libre d'impôt pour l'achat d'une première propriété (CELIAPP)",
      icon: Home,
      to: "/first-home-savings-account",
    },
    {
      title: "Régime enregistré d'épargne-études (REEE)",
      icon: GraduationCap,
      to: "/registered-education-savings-plan",
    },
    {
      title: "Compte d'épargne libre d'impôt (CELI)",
      icon: Wallet,
      to: "/tax-free-savings-account",
    },
    {
      title: "Régime enregistré d'épargne-retraite (REER)",
      icon: Banknote,
      to: "/registered-retirement-savings-plan",
    },

    {
      title: "Régime enregistré d'épargne-invalidité (REEI)",
      icon: PiggyBank,
      to: "/registered-disability-savings-plan",
    },
  ];

  return (
    <>
      <SEO
        title="Produits d'assurance et de placement au Canada – einsured.ca"
        description="Comparez les produits d’assurance et de placement de compagnies de confiance partout au Canada et choisissez la solution adaptée à vos besoins avec einsured.ca."
        keywords="produits d'assurance et de placement, assurance vie, assurance voyage, CELIAPP, REEE, REER Canada"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20 font-[Inter,sans-serif]">
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="text-2xl sm:text-3xl md:text-6xl font-bold text-[#49628b] mb-3">
            Tout ce que nous offrons
          </h1>
          <p className="text-gray-600 mt-3 sm:mt-4 max-w-3xl mx-auto text-[15px] sm:text-[17px] leading-relaxed">
            Nous offrons tous les principaux produits d'assurance et de placement proposés par des compagnies reconnues et de confiance partout au Canada.
          </p>
        </div>

        {/* Assurance */}
        <SectionTitle>Assurance</SectionTitle>
        <CategoryTitle>Vie</CategoryTitle>
        <CardGrid items={life} />

        <CategoryTitle>Voyage</CategoryTitle>
        <CardGrid items={travel} />

        <CategoryTitle>Prestations du vivant</CategoryTitle>
        <CardGrid items={livingBenefits} />

        {/* Placements */}
        <CategoryTitle>Placements</CategoryTitle>
        <CardGrid items={investments} />
      </section>
    </>
  );
}
